import bigDecimal from 'js-big-decimal'

/**
 * Big Decimal Helper Utilities
 * Provides clean, reusable wrapper functions for js-big-decimal operations
 */

/**
 * Converts a value to a valid big decimal string, treating null/undefined/empty as '0'
 */
export const bd = (n: string | number | null | undefined): string => {
  return n == null || n === '' ? '0' : String(n)
}

/**
 * Adds two numbers using big decimal precision
 */
export const bdAdd = (a: string | number, b: string | number): string => {
  return bigDecimal.add(bd(a), bd(b))
}

/**
 * Subtracts b from a using big decimal precision
 */
export const bdSub = (a: string | number, b: string | number): string => {
  return bigDecimal.subtract(bd(a), bd(b))
}

/**
 * Multiplies two numbers using big decimal precision
 */
export const bdMul = (a: string | number, b: string | number): string => {
  return bigDecimal.multiply(bd(a), bd(b))
}

/**
 * Divides a by b using big decimal precision
 * @param prec - Precision for division (default: 12)
 * Returns '0' if divisor is zero
 */
export const bdDiv = (a: string | number, b: string | number, prec = 12): string => {
  return bd(b) === '0' ? '0' : bigDecimal.divide(bd(a), bd(b), prec)
}

/**
 * Returns the floor (rounded down) value of a number
 */
export const bdFloor = (n: string | number): string => {
  return String(bigDecimal.floor(bd(n)))
}

/**
 * Returns the ceiling (rounded up) value of a number
 */
export const bdCeil = (n: string | number): string => {
  return String(bigDecimal.ceil(bd(n)))
}

/**
 * Rounds a number to the specified number of decimal places
 */
export const bdRound = (n: string | number, precision = 0): string => {
  return bigDecimal.round(bd(n), precision, 5)
}

/**
 * Returns the maximum of two numbers
 */
export const bdMax = (a: string | number, b: string | number): string => {
  return bigDecimal.compareTo(bd(a), bd(b)) >= 0 ? bd(a) : bd(b)
}

/**
 * Returns the minimum of two numbers
 */
export const bdMin = (a: string | number, b: string | number): string => {
  return bigDecimal.compareTo(bd(a), bd(b)) <= 0 ? bd(a) : bd(b)
}

/**
 * Compares two numbers
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b
 */
export const bdCompare = (a: string | number, b: string | number): number => {
  return bigDecimal.compareTo(bd(a), bd(b))
}

/**
 * Checks if two numbers are equal
 */
export const bdEquals = (a: string | number, b: string | number): boolean => {
  return bigDecimal.compareTo(bd(a), bd(b)) === 0
}

/**
 * Checks if a is greater than b
 */
export const bdGreaterThan = (a: string | number, b: string | number): boolean => {
  return bigDecimal.compareTo(bd(a), bd(b)) > 0
}

/**
 * Checks if a is less than b
 */
export const bdLessThan = (a: string | number, b: string | number): boolean => {
  return bigDecimal.compareTo(bd(a), bd(b)) < 0
}

/**
 * Converts a big decimal string to a number
 * Returns 0 if conversion fails
 */
export const toNum = (s: string | number): number => {
  return parseFloat(String(s)) || 0
}

/**
 * Clamps a value between min and max
 */
export const bdClamp = (value: string | number, min: string | number, max: string | number): string => {
  return bdMax(min, bdMin(max, value))
}

/**
 * Calculates percentage of a value
 * @param value - The base value
 * @param percent - The percentage (e.g., 10 for 10%)
 */
export const bdPercent = (value: string | number, percent: string | number): string => {
  return bdMul(bdMul(value, percent), '0.01')
}

/**
 * Calculates what percentage one value is of another
 * Returns 0 if total is zero
 */
export const bdPercentOf = (value: string | number, total: string | number): string => {
  return bd(total) === '0' ? '0' : bdDiv(bdMul(value, '100'), total)
}

/**
 * Returns the absolute value of a number
 */
export const bdAbs = (n: string | number): string => {
  const val = bd(n)
  return bigDecimal.compareTo(val, '0') < 0 ? bigDecimal.multiply(val, '-1') : val
}

/**
 * Negates a number
 */
export const bdNegate = (n: string | number): string => {
  return bigDecimal.multiply(bd(n), '-1')
}